import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { store as appStore } from '../../../redux/store';
import { clearSelection } from '../../../redux/selectionSlice';
import * as THREE from 'three';
import { COMMANDS } from '../../../utils/commonProperties';
import { setCommandMessage, resetOperation, setCommandType, setOperationData } from '../../../redux/operationSlice';
import { addLoadToComponent, addComponent, updateComponent } from '../../../redux/simpleSupplySlice';
import { addWire } from '../../../redux/wireSlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { COMPONENT_KINDS } from '../../../types/SimpleSupplyPoint';
import { createLightSwich } from '../../../factory/symbols/createLightingSymbols';
import { singleCircleBuatForLight, singleSquareBuat } from '../../../factory/symbols/createLightingSymbols';
import parseColor from '../../../utils/parseColor';
import { updatePanelTitles } from '../../../modules/wiring';
import useSnapPoints from '../../../shared/snap/useSnapPoints';
import { createLightingFeeder } from '../../../factories/FeederFactory';
import { useComponentWiring } from '../../../modules/wiring';
import { LightingCommand } from '../../../modules/undoRedo';
import { selectActiveFloorPlan, getRepresentedFloors } from '../../../redux/floorPlanSlice';
import { getObjectById } from '../../../utils/getObjectById';
import { findObjectByUUID } from '../../../utils/findObjectByUUID';

const useLighting = (scene, camera, renderer, undoRedoManagerRef) => {
  const dispatch = useDispatch();
  const { commandType, data: operationData } = useSelector((state) => state.operation);
  // simpleSupply: useSelector kaldırıldı — event handler'larda lazy read
  const activeFloorPlan = useSelector(selectActiveFloorPlan);
  const { snapPoints } = useSnapPoints(scene);
  // Read parsed wire color from settings so persistent wires use same color as preview
  const settings = useSelector((state) => state.settings);
  const parsedWire = parseColor(settings?.lightingSwitch?.wireColor ?? 0x0045ff);

  const dynamicSnapPoints = useRef([]);
  const wiringActiveRef = useRef(false);
  const sceneRef = useRef(null);
  const placedLightSwitchesRef = useRef([]);

const lightingConfig = {
  commandType: COMMANDS.DRAW_LIGHT,
  itemTypeName: 'lightSwitch',
  wireColor: parseColor(settings?.lightingSwitch?.wireColor ?? 0x0045ff).colorNumber,
  allowSpaceKey: true, // ✅ Lighting'de Space tuşu ile çoklu anahtar eklenebilir
  markerCount: 2, // ✅ Aydınlatma hattı 2 iletkenli (priz 3, makine 3)
  markerSkipFirstSegment: true, // ✅ preview = permanent (ilk/panel edge'inde marker yok)
  itemTypes: [
    { name: 'Normal Anahtar', type: 'normal', power: 0 },
    { name: 'Komütatör', type: 'komütatör', power: 0 },
    { name: 'Vaviyen', type: 'vaviyen', power: 0 },
    { name: 'Etanj', type: 'etanj', power: 0 },
    { name: 'Etanj Komütatör', type: 'etanj komütatör', power: 0 },
  ],
  createSymbol: (position, direction, angle, isPreview, itemType, extraData = {}) => {
    const colorSetting = isPreview
      ? (settings?.lightingSwitch?.previewColor ?? settings?.lightingSwitch?.color ?? 0x0045ff)
      : (settings?.lightingSwitch?.color ?? 0x0045ff);
    const parsed = parseColor(colorSetting);
    const rotateValueDeg = extraData.rotateValue || 0;

    return createLightSwich(
      null,
      position,
      direction,
      parsed.colorNumber,
      isPreview,
      itemType?.type || 'normal',
      null,
      rotateValueDeg
    );
  },
  createCommand: (action, componentData, feederData, sceneData) => {
    // Optionally add rotateValue persist logic if needed
    return new LightingCommand(action, componentData, feederData, sceneData);
  },
  onKeyDown: null // Şimdilik boş bırakılabilir veya rota döndürme gibi şeyler eklenebilir
};

  // === Yardımcı: sahneye görsel aydınlatma anahtarı yerleştir
  const handleLightSwitchPlace = (position, direction) => {
    const adjustedPos = position.clone().add(
      new THREE.Vector3(-direction.x * 7, -direction.y * 14, 0)
    );
  const lightSwitch = createLightSwich(sceneRef.current, adjustedPos, direction, parsedWire.colorNumber, false, "normal", null, 45);

  const angle = Math.atan2(direction.y, direction.x);
  // Parent group rotation should represent direction; inner offsetGroup holds user rotate offset
  try { lightSwitch.rotation.z = angle; } catch (e) { }

    const id = generateUniqueId("lightSwitch");
    lightSwitch.userData = {
      ...lightSwitch.userData,
      id,
      kind: COMPONENT_KINDS.SWITCH_LINE,
      type: 'LightSwitch',
      isSelectable: true,
      originalColor: parsedWire.colorNumber,
      lightingSubType: operationData?.initialItemType || null,
    };

    // 🆕 Redux'a component ekle
    dispatch(addComponent({
      id,
      kind: COMPONENT_KINDS.SWITCH_LINE,
      electrical: { installedPower: 0 },
      incomingFeeders: [],
      outgoingFeeders: []
    }));

    sceneRef.current.add(lightSwitch);
    return lightSwitch;
  };

  // === Aydınlatma anahtarı sil
  const handleLightSwitchRemove = (lightSwitch) => {
    if (!lightSwitch) return;
    sceneRef.current.remove(lightSwitch);
    lightSwitch.traverse(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });
  };

  // === Lighting placement confirmation
  const confirmPlacement = (vertices, lightSwitches, source, tempLineUUID, markers, originalVertices) => {
    // 🆕 REDO kontrolü: REDO sırasında yeni transaction başlatma
    // REDO'da command zaten var, sadece notifyRedoComplete çağrılacak
    const isRedoMode = operationData?.isRedo;
    if (isRedoMode) {
      return;
    }

    // Source tipine göre parent panel ID'yi belirle
    let parentId = null;
    let sourceType = null;
    let parentLightingLoadId = null;
    let parentWireID = null;

    if (source?.type === 'panelConnection' && source?.p) {
      // Panel çıkışından başlatıldı
      parentId = source.p.panelID;
      sourceType = 'panel';
    } else if (source?.type === 'lightingBuat') {
      // Mevcut aydınlatma buatından başlatıldı (dallanma)
      parentId = source.panelID || source.userData?.panelID;
      parentLightingLoadId = source.lightingLoadId || source.userData?.lightingLoadId;
      parentWireID = source.wireID || source.userData?.wireID;
      sourceType = 'lightingBuat';
 
    } else if (source?.type === 'vertex' || source?.type === 'start' || source?.type === 'end') {
      // Aydınlatma hattının vertex'inden başlatıldı (dallanma). YALNIZ gerçek
      // aydınlatma hattı vertex'i geçerli — duvar/başka-tip çizgi köşesi DEĞİL.
      // source.parent doğrudan userData objesi, THREE.js objesi değil.
      const parentData = source.parent;
      if (parentData && (parentData.function === 'simpleLighting' || parentData.lightingLoadId != null)) {
        parentId = parentData.panelID;
        parentLightingLoadId = parentData.lightingLoadId;
        parentWireID = parentData.id || parentData.wireID;
        sourceType = 'lightingVertex';
      }
    } else if (source?.type === 'cableShaftConnection' && operationData?.continueFromShaft) {
      // ✅ Kablo bacasından devam eden hat - kaynak paneli operationData'dan al
      const sPanelId = operationData?.sourceCable?.sourcePanelId;
      if (sPanelId) {
        parentId = sPanelId;
        sourceType = 'continueFromShaft';
      }
    }

    if (!parentId) {
      console.warn('[confirmPlacement DEBUG] parentId is null! sourceType:', sourceType, 'source?.type:', source?.type);
        return;
    }
    let branchID;
    let power = 0

    // 🆕 Lighting load component bilgileri - önce tanımla
    // Dallanma durumunda parent ID'yi kullan, yoksa yeni ID oluştur
    const lightingLoadId = (sourceType === 'lightingBuat' || sourceType === 'lightingVertex')
      ? parentLightingLoadId  // Mevcut hatta dallanma - parent ID kullan
      : generateUniqueId('lighting_load'); // Panel'den yeni hat - yeni ID

    // Son anahtarın koordinatını bul ve vertices'i ona kadar kısalt
    let finalVertices = [...vertices];
    if (lightSwitches.length > 0) {
      const lastLightSwitch = lightSwitches[lightSwitches.length - 1];
      const lastLightSwitchPos = lastLightSwitch.position;

      // Son anahtar koordinatını vertices dizisinde bul
      let lastLightSwitchIndex = -1;
      for (let i = 0; i < vertices.length; i++) {
        const vertex = vertices[i];
        if (Math.abs(vertex.x - lastLightSwitchPos.x) < 0.01 &&
          Math.abs(vertex.y - lastLightSwitchPos.y) < 0.01) {
          lastLightSwitchIndex = i;
          break;
        }
      }

      // Son anahtardan sonraki noktaları sil
      if (lastLightSwitchIndex !== -1) {
        finalVertices = vertices.slice(0, lastLightSwitchIndex + 1);
      }
    }

    // Kısaltılmış vertices ile uzunluk hesapla
    let totalLength = 0;
    for (let i = 0; i < finalVertices.length - 1; i++) {
      totalLength += finalVertices[i].distanceTo(finalVertices[i + 1]);
    }

  // Determine persistent wire color from settings (already parsed in outer scope)
  const geometry = new THREE.BufferGeometry().setFromPoints(finalVertices);
  const material = new THREE.LineBasicMaterial({ color: parsedWire.colorNumber, transparent: parsedWire.alpha < 1, opacity: parsedWire.alpha });
  const line = new THREE.Line(geometry, material);

    const feederId = generateUniqueId("feederLighting");
    const wireID = generateUniqueId("wireLighting");
    const lightSwitchIds = lightSwitches.map(s => s.id || s.userData?.id);
    const lightSwitchIdsMap = lightSwitchIds.reduce((acc, id) => { acc[id] = true; return acc; }, {});

    // ✅ Buat ID listesi (her anahtar için bir buat olacak)
    const buatIdsList = [];
    const buatIdsMap = {};

    // ✅ Wire Marker ID listesi
    const wireMarkerIdsList = [];
    const wireMarkerIdsMap = {};

    line.userData = {
      id: wireID,
      feederId: feederId, // ✅ Redux feeder ID eşleştirmesi için
      type: 'wireLighting',
      function: 'simpleLighting',
      panelID: parentId,
      lightSwitches: lightSwitchIds,
      sockets: lightSwitchIds, // ✅ Gizmo uyumluluğu için
      socketIdsMap: lightSwitchIdsMap, // ✅ Gizmo uyumluluğu için
      buatIds: buatIdsList, // ✅ Buat ID listesi
      buatIdsMap: buatIdsMap, // ✅ Buat ID map
      wireMarkerIds: wireMarkerIdsList, // ✅ Wire Marker ID listesi
      wireMarkerIdsMap: wireMarkerIdsMap, // ✅ Wire Marker ID map
      length: totalLength.toFixed(2),
      lightingLoadId: lightingLoadId, // 🔥 Armatür hookları için gerekli
      isSelectable: true,
      sourceType: sourceType, // Panel'den mi, lighting buat'tan mı başladı
      vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z })), // Snap points için vertex bilgileri
      lightingSubType: operationData?.initialItemType || null, // ✅ Kablo bacası fixture akışı için
    };

    scene.add(line);

    // ✅ KALICI WIRE MARKER'LAR EKLE (150 birimden büyük segmentler için)
    const rotateVector = (vector, angleRad) => {
      const cos = Math.cos(angleRad);
      const sin = Math.sin(angleRad);
      return new THREE.Vector3(
        vector.x * cos - vector.y * sin,
        vector.x * sin + vector.y * cos,
        0
      );
    };
    // Panel objesini bul
    let panel = null;
    if (sourceType === 'panel') {
      const foundPanelOutSnap = findObjectByUUID(scene, source.p.UUID);
      panel = source?.userData?.sourceObject ?? foundPanelOutSnap;
    } else if (sourceType === 'continueFromShaft') {
      // ✅ Kablo bacasından devam - kaynak paneli scene'den bul
      scene.traverse(obj => {
        if (obj.userData?.id === parentId) {
          panel = obj;
        }
      });
    } else if (sourceType === 'lightingBuat' || sourceType === 'lightingVertex') {
      // Lighting buat veya vertex'ten başlatıldıysa, parent paneli bul
      panel = getObjectById(scene, parentId);

      if (!panel) {
        // Alternatif: Scene'i tara ve paneli bul
        scene.traverse(obj => {
          if (obj.userData?.id === parentId) {
            panel = obj;
          }
        });
      }
    }

    branchID = generateUniqueId("branchLighting");

    // ✅ Wire userData'ya branchID ekle (panelExitManager wire eşleştirmesi için)
    line.userData.branchID = branchID;

    // Panel var mı kontrol et
    if (!panel) {
      console.error('[confirmPlacement DEBUG] PANEL NOT FOUND! sourceType:', sourceType, 'parentId:', parentId);
      return;
    }

    // Panel userData var mı kontrol et
    if (!panel.userData) {
      panel.userData = {};
    }

    // Panel userData.outs array'ini kontrol et, yoksa oluştur
    if (!panel.userData.outs) {
      panel.userData.outs = [];
    }


    // ✅ Command için component ve feeder verilerini scope dışında tanımla
    let commandComponentData = null;
    let commandFeederData = null;

    // ✅ lightingSubType varsa (Merdiven/Asansör Aydınlatma) aktif katı floorArmatures'a yaz
    const initialFloorArmatures = {};
    if (operationData?.initialItemType) {
      let cFloorName = null;
      if (sourceType === 'continueFromShaft') {
        cFloorName = operationData?.currentFloorPlanName || operationData?.sourceFloorPlanName;
      } else if (activeFloorPlan) {
        const floors = getRepresentedFloors(activeFloorPlan);
        cFloorName = floors.length > 0 ? floors[0] : activeFloorPlan.name;
      }
      if (cFloorName) {
        initialFloorArmatures[cFloorName] = {
          count: 0, // Armatür sayısı: armatürler eklendiğinde fixtureCount'tan okunacak
          power: 0
        };
      }
    }

    // ✅ Dallanma kontrolü: Mevcut hatta ekleniyorsa parent component'i güncelle
    if (sourceType === 'lightingBuat' || sourceType === 'lightingVertex') {
      // Mevcut bir hatta dallanma - parent lighting load'ı bul ve güncelle
      const parentComponent = appStore.getState().simpleSupply.components?.find(c => c.id === parentLightingLoadId);

      if (parentComponent) {
        // Mevcut component'in gücünü ve switch sayısını güncelle
        const updatedPower = (parentComponent.electrical?.installedPower || 0) + power;
        const updatedSwitchCount = (parentComponent.metadata?.switchCount || 0) + lightSwitches.length;

        dispatch(addLoadToComponent({
          parentComponentId: panel.userData?.id || panel.id,
          loadData: {
            ...parentComponent,
            electrical: {
              ...parentComponent.electrical,
              installedPower: updatedPower,
              demandPower: updatedPower,
              current: updatedPower / (220 * 1)
            },
            metadata: {
              ...parentComponent.metadata,
              switchCount: updatedSwitchCount
            }
          },
          feederData: null, // Yeni feeder ekleme, sadece component güncelle
          updateExisting: true // Component güncelleme modu
        }));

        // ✅ Dallanma için mevcut component'i command'a geç
        commandComponentData = { ...parentComponent, id: parentComponent.id };
        commandFeederData = null;

      } else {
        // Parent bulunamadıysa yeni component oluştur (fallback)
        const newLightingLoadData = {
          id: lightingLoadId,
          name: 'Aydınlatma Hattı',
          branchType: 'lighting',
          power: power,
          phases: 1,
          electrical: {
            installedPower: power,
            demandPower: power,
            current: power / (220 * 1),
            phaseCount: 1,
            demandFactor: "100%"
          },
          parentComponentId: panel.userData?.id || panel.id,
          metadata: {
            wireId: wireID,
            branchId: branchID,
            length: totalLength.toFixed(2),
            switchCount: lightSwitches.length,
            loadType: 'lighting',
            lightingLoadId: lightingLoadId,
            lightingSubType: operationData?.initialItemType || null,
          }
        };

        const lightingFeeder = createLightingFeeder(
          power,
          panel.userData?.id || panel.id,
          lightingLoadId,
          {
            id: feederId,
            vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
            cable: {
              length: Math.round(totalLength / 10)
            },
            metadata: {
              branchType: 'lightSwitch',
              length: totalLength.toFixed(2),
              wireId: wireID
            }
          }
        );

        dispatch(addLoadToComponent({
          parentComponentId: panel.userData?.id || panel.id,
          loadData: newLightingLoadData,
          feederData: lightingFeeder
        }));

        // ✅ Command için verileri kaydet
        commandComponentData = newLightingLoadData;
        commandFeederData = lightingFeeder;
      }
    } else {
      // Panel'den başlayan yeni hat - normal akış
      const newLightingLoadData = {
        id: lightingLoadId,
        name: 'Aydınlatma Hattı',
        branchType: 'lighting',
        power: power,
        phases: 1,
        electrical: {
          installedPower: power,
          demandPower: power,
          current: power / (220 * 1),
          phaseCount: 1,
          demandFactor: "100%"
        },
        parentComponentId: panel.userData?.id || panel.id,
        metadata: {
          wireId: wireID,
          branchId: branchID,
          length: totalLength.toFixed(2),
          switchCount: lightSwitches.length,
          loadType: 'lighting',
          lightingLoadId: lightingLoadId,
          lightingSubType: operationData?.initialItemType || null,
          ...(Object.keys(initialFloorArmatures).length > 0 ? { floorArmatures: initialFloorArmatures } : {}),
        }
      };

      const lightingFeeder = createLightingFeeder(
        power,
        panel.userData?.id || panel.id,
        lightingLoadId,
        {
          id: feederId,
          vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
          cable: {
            length: Math.round(totalLength / 10)
          },
          metadata: {
            branchType: 'lightSwitch',
            length: totalLength.toFixed(2),
            wireId: wireID
          }
        }
      );

      dispatch(addLoadToComponent({
        parentComponentId: panel.userData?.id || panel.id,
        loadData: newLightingLoadData,
        feederData: lightingFeeder
      }));

      // ✅ Command için verileri kaydet
      commandComponentData = newLightingLoadData;
      commandFeederData = lightingFeeder;
    }


    // ESKİ SİSTEM: Legacy compat için hala userData'ya da ekle
    // ⚠️ Sadece panel'den direkt çıkan hatlar için outs'a ekle, dallanmalarda ekleme
    if (sourceType === 'panel') {
      const exitData = {
        branchID,
        power,
        branchType: "lightSwitch",
        description: "lightingLine",
        length: totalLength.toFixed(2),
        mcbReference: 433, // MCB numarası eklendi
        feederId: feederId, // ✅ Tek Hat Şeması için feederId eklendi
        lineType: "lightSwitch", // ✅ Transform fonksiyonu için lineType eklendi
        lightingSubType: operationData?.initialItemType || null, // ✅ Merdiven/Asansör Aydınlatma türü
        floorArmatures: { ...initialFloorArmatures }, // ✅ Kat başına armatür (aktif kat ile başlat)
        wires: [{
          id: wireID,
          length: totalLength.toFixed(2),
          power
        }]
      };

      // ✅ addPosition'a göre diziye ekleme yap
      const addPosition = operationData?.addPosition || 'start';
      if (addPosition === 'end') {
        // "Başa ekle" seçili - dizinin BAŞINA ekle
        panel.userData.outs.unshift(exitData);
      } else {
        // "Sona ekle" seçili (default) - dizinin SONUNA ekle
        panel.userData.outs.push(exitData);
      }
    } else if (sourceType === 'continueFromShaft') {
      // ✅ Kablo bacasından devam eden hat: floorArmatures güncelle
      const currentFloorName = operationData?.currentFloorPlanName || operationData?.sourceFloorPlanName || null;
      const subType = operationData?.initialItemType;

      if (subType && currentFloorName) {
        // 1) panel.userData.outs'daki ilgili çıkışı güncelle (varsa)
        if (panel?.userData?.outs) {
          const exitIdx = panel.userData.outs.findIndex(out => out.lightingSubType === subType);
          if (exitIdx >= 0) {
            if (!panel.userData.outs[exitIdx].floorArmatures) {
              panel.userData.outs[exitIdx].floorArmatures = {};
            }
            panel.userData.outs[exitIdx].floorArmatures[currentFloorName] = {
              count: 0, // Armatür eklendiğinde fixtureCount'tan güncellenecek
              power: 0
            };
          }
        }

        // 2) Redux simpleSupply'daki ilgili load'un metadata'sını güncelle
        // Önce mevcut load'u bul, bulunamazsa az önce oluşturulan yeni load'u kullan
        const matchingLoad = appStore.getState().simpleSupply.components?.find(c =>
          c.parentComponentId === parentId &&
          c.metadata?.lightingSubType === subType
        );
        const targetLoadId = matchingLoad?.id || lightingLoadId;
        const existingFloorArm = matchingLoad?.metadata?.floorArmatures || {};
        const updatedFloorArmatures = {
          ...existingFloorArm,
          [currentFloorName]: {
            count: 0,
            power: 0
          }
        };
        dispatch(updateComponent({
          id: targetLoadId,
          changes: {
            metadata: {
              floorArmatures: updatedFloorArmatures
            }
          }
        }));
      }
    }
    // Refresh panel titles visually so panel returns to normal state
    try {
      if (panel && panel.userData && panel.userData.id) updatePanelTitles(scene, panel.userData.id);
    } catch (e) {}

    // İlk edge'i atla (yeşil nokta taşıması için) - finalVertices kullan
    for (let i = 1; i < finalVertices.length - 1; i++) {
      const p1 = finalVertices[i];
      const p2 = finalVertices[i + 1];
      const dir = new THREE.Vector3().subVectors(p2, p1);
      const length = dir.length();

      if (length < 150) continue;

      const normal = dir.clone().normalize();
      const edgeAngle = Math.atan2(normal.y, normal.x); // Radyan cinsinden kenar açısı
      const angleRad = THREE.MathUtils.degToRad(45); // 45 derece açı
      const angledDir = rotateVector(normal, angleRad);
      const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

      const forwardOffset = normal.clone().setLength(1.5); // Çizgiler arası mesafe
      const halfLength = 2.5;

      // ✅ 2 marker positions - lighting için (machine 3 kullanır, lighting 2)
      const positions = [
        mid.clone().sub(forwardOffset),
        mid.clone().add(forwardOffset),
      ];

      // ✅ Marker Group Oluştur - MachineCommand pattern'i
      const markerGroup = new THREE.Group();
      markerGroup.position.set(0, 0, 0); // ✅ World coordinates (sıfır)

      markerGroup.userData = {
        role: 'wireMarker', // ✅ Tutarlı rol adı
        parentWireId: wireID, // ✅ feederId değil wireID (wire.userData.id ile eşleşmeli)
        type: 'permanent',
        id: markerGroup.uuid,
        edgeIndex: i, // ✅ updateGizmos için gerekli
        lastEdgeAngle: edgeAngle + angleRad, // ✅ Başlangıç açısı
        midPoint: mid.clone(), // ✅ Orta nokta referansı
        isSelectable: false, // ✅ Wire marker'lar seçilemez (gizmo oluşmamalı)
        isGizmo: false // ✅ Gizmo değil
      };

      // ✅ Her center point için angled line oluştur - world coordinates
      positions.forEach((centerPoint) => {
        const start = centerPoint.clone().add(angledDir.clone().multiplyScalar(-halfLength));
        const end = centerPoint.clone().add(angledDir.clone().multiplyScalar(halfLength));

        const geo = new THREE.BufferGeometry().setFromPoints([start, end]);
        const mat = new THREE.LineBasicMaterial({ color: 0xffffff });
        const markerLine = new THREE.Line(geo, mat);

        markerGroup.add(markerLine);
      });

      // ✅ Listeye ve Map'e ekleme
      wireMarkerIdsList.push(markerGroup.uuid);
      wireMarkerIdsMap[markerGroup.uuid] = true;

      scene.add(markerGroup);
    }
    const lastLightSwitch = lightSwitches[lightSwitches.length - 1];
    const targetId = lastLightSwitch?.userData?.id || lastLightSwitch?.id;
    // 🗑️ DUPLICATE COMPONENT/FEEDER EKLEME KALDIRILDI
    // addLoadToComponent zaten component ve feeder ekliyor

    // Redux wireSlice kaydı - Kısaltılmış vertices kullan
    const wireColorHex = '#' + parsedWire.colorNumber.toString(16).padStart(6, '0');
    dispatch(addWire({
      id: feederId,
      sceneUUID: line.uuid,
      type: "polyline",
      vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
      layer: "lightSwitch",
      color: wireColorHex
    }));

    // ✅ Aydınlatma anahtarlarına lighting buat ekle (son anahtar yuvarlak buat)
    lightSwitches.forEach((lightSwitch, index) => {
      const lightSwitchPosition = lightSwitch.position;
      const lightSwitchId = lightSwitch.userData?.id || lightSwitch.id;
      const isLastSwitch = index === lightSwitches.length - 1;

      // ✅ Buat ID'yi üret
      const buatId = generateUniqueId('buat');

      // Son anahtar için yuvarlak buat, diğerleri için kare buat
      const buat = isLastSwitch
        ? singleCircleBuatForLight(
            scene,
            { x: lightSwitchPosition.x, y: lightSwitchPosition.y },
            parsedWire.colorNumber,
            lightSwitch.uuid
          )
        : singleSquareBuat(
            scene,
            { x: lightSwitchPosition.x, y: lightSwitchPosition.y },
            parsedWire.colorNumber,
            lightSwitch.uuid
          );

      // Buat'ı lightingBuat olarak işaretle
      buat.userData = {
        ...buat.userData,
        id: buatId, // ✅ ID ekle
        type: isLastSwitch ? 'buatCircle' : 'lightingBuat',
        buatType: isLastSwitch ? 'circle' : 'square',
        function: 'simpleLighting',
        lightingLoadId: lightingLoadId,
        wireID: wireID,
        panelID: parentId,
        lightingSubType: operationData?.initialItemType || null, // ✅ Kablo bacası fixture akışı için
      };

      scene.add(buat);

      // ✅ Buat ID'yi listeye ekle
      buatIdsList.push(buatId);
      buatIdsMap[buatId] = true;
    });

    // ✅ Dallanma noktasına buat ekle veya güncelle
    if (sourceType === 'lightingVertex' || sourceType === 'lightingBuat') {
      const branchPoint = finalVertices[0]; // İlk vertex = dallanma noktası

      // Bu noktada zaten bir buat var mı kontrol et
      let existingBuat = null;
      scene.traverse(obj => {
        if ((obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') &&
            Math.abs(obj.position.x - branchPoint.x) < 0.01 &&
            Math.abs(obj.position.y - branchPoint.y) < 0.01) {
          existingBuat = obj;
        }
      });

      if (existingBuat) {
        // Mevcut buat var
        const isCircleBuat = existingBuat.userData?.buatType === 'circle' ||
                            existingBuat.userData?.type === 'buatCircle' ||
                            !existingBuat.userData?.buatType; // buatType tanımlı değilse varsayılan circle

        // ✅ Başlangıç buatına lighting wire mapId ekle (userData property read-only ise defineProperty ile zorla)
        try {
          const newUserData = {
            ...existingBuat.userData,
            lightingWireMapIds: [
              ...(existingBuat.userData.lightingWireMapIds || []),
              wireID
            ]
          };

          // userData read-only olabilir, defineProperty ile override et
          Object.defineProperty(existingBuat, 'userData', {
            value: newUserData,
            writable: true,
            configurable: true,
            enumerable: true
          });

        } catch (err) {
          // Buat userData update failed
        }

        // Yuvarlak buat'a yeni hat ekleniyorsa kare buat'a dönüştür
        if (isCircleBuat) {
          const oldPosition = existingBuat.position.clone();
          const oldUserData = existingBuat.userData;
          const buatId = oldUserData.id || generateUniqueId('buat'); // ✅ Mevcut ID'yi koru veya yeni üret

          // Eski buat'ı kaldır
          scene.remove(existingBuat);
          existingBuat.traverse(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
          });

          // Kare buat oluştur
          const squareBuat = singleSquareBuat(
            scene,
            { x: oldPosition.x, y: oldPosition.y },
            parsedWire.colorNumber,
            oldUserData.lightSwitchId || generateUniqueId('buat')
          );

          squareBuat.userData = {
            ...oldUserData,
            id: buatId, // ✅ ID'yi ekle
            type: 'lightingBuat',
            buatType: 'square'
          };

          scene.add(squareBuat);
        }
      } else {
        // Bu noktada buat yok - yeni yuvarlak buat ekle
        const buatId = generateUniqueId('buat'); // ✅ Yeni ID üret

        const newBuat = singleCircleBuatForLight(
          scene,
          { x: branchPoint.x, y: branchPoint.y },
          parsedWire.colorNumber,
          generateUniqueId('buat')
        );

        newBuat.userData = {
          id: buatId, // ✅ ID'yi ekle
          type: 'buatCircle',
          buatType: 'circle',
          function: 'simpleLighting',
          lightingLoadId: parentLightingLoadId || lightingLoadId,
          wireID: parentWireID,
          panelID: parentId,
          lightingWireMapIds: [wireID], // ✅ İlk wire mapId'sini ekle
          lightingSubType: operationData?.initialItemType || null, // ✅ Kablo bacası fixture akışı için
        };

        scene.add(newBuat);

        // ✅ Branch (ek) buat'ı transaction'a bağla: buatIdsMap/List'e ekle ki aşağıdaki
        // buatInfos traverse'i onu yakalasın → LightingCommand undo/redo bu buat'ı da
        // yönetsin. Yoksa ek alınan hatta bu ilk buat undo'da sahnede kalıyordu
        // (wireID = parentWireID olduğu için wireID-tabanlı silme onu kaçırıyordu).
        buatIdsMap[buatId] = true;
        buatIdsList.push(buatId);
      }
    }

    // Global Undo/Redo Transaction
    // ✅ Runtime'da undoRedoManager'ı kontrol et (ref'ten veya window'dan)
    const undoRedoManager = undoRedoManagerRef.current || (typeof window !== 'undefined' ? window.undoRedoManager : null);

    if (undoRedoManager) {
      
      const tx = undoRedoManager.beginTransaction('Add Lighting Wire');

      // Restore callback: undo sonrası draw modunu yeniden aç
      const restoreCallback = (restoreVertices, restoreSource, isRedoContext = false, sceneData = null) => {
        dispatch(setCommandType(COMMANDS.DRAW_LIGHT));
        setTimeout(() => {
          const operationData = {
            initialPoint: restoreVertices && restoreVertices[0] ? {
              x: restoreVertices[0].x,
              y: restoreVertices[0].y,
              z: restoreVertices[0].z || 0
            } : null,
            source: restoreSource,
            _restoreId: Date.now() + Math.random()
          };

          // ✅ REDO mode: İlk vertex + geri kalanı vertexHistory'ye yükle
          if (isRedoContext) {
            operationData.restoreVertices = [restoreVertices[0]];
            operationData.vertexHistory = restoreVertices.slice(1);
            operationData.isRedo = true;
            operationData.lightSwitchInfos = sceneData?.lightSwitchInfos || [];
            operationData.buatInfos = sceneData?.buatInfos || [];
            operationData.onRedoComplete = sceneData?.onRedoComplete;
            operationData.wireColor = sceneData?.color; // ✅ Buat rengi için wire color
          } else {
            // ✅ UNDO mode: Tüm vertex'leri restore et
            operationData.restoreVertices = restoreVertices;
            operationData.isRedo = false;
            operationData.lightSwitchInfos = restoreSource?.lightSwitchInfos || sceneData?.lightSwitchInfos || [];
            operationData.buatInfos = sceneData?.buatInfos || [];
            operationData.branchID = sceneData?.branchId || sceneData?.branchID;
            operationData.wireId = sceneData?.wireId || sceneData?.wireID;
            operationData.feederId = sceneData?.wireId;
            operationData.loadId = sceneData?.lightingLoadId;
            operationData.wireColor = sceneData?.color; // ✅ Buat rengi için wire color
          }

          dispatch(setOperationData(operationData));
        }, 16);
      };

      // Tüm wire + lightSwitch'ler için tek bir komut oluştur
      // Wire bilgisi
      const wireData = {
        id: feederId,
        sceneUUID: line.uuid,
        type: "polyline",
        vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
        layer: "lightSwitch",
        color: '#' + parsedWire.colorNumber.toString(16).padStart(6, '0'),
        lightingLoadId: lightingLoadId,
        wireID: wireID,
        panelId: parentId // ✅ Panel çıkış yönetimi için gerekli (REDO'da updatePanelWiringPositions kullanır)
      };

      // Tüm lightSwitch ID'lerini ve bilgilerini topla - scene'den bul
      const lightSwitchIds = [];
      const lightSwitchInfos = []; // ✅ Restore için lightSwitch bilgileri

      lightSwitches.forEach(lightSwitchItem => {
        const compId = lightSwitchItem?.uuid || lightSwitchItem?.id || lightSwitchItem?.userData?.id || null;
        let foundObj = null;
        if (compId) {
          scene.traverse(obj => {
            if (!foundObj && obj.userData?.id === compId) foundObj = obj;
          });
        }

        if (!foundObj && lightSwitchItem && lightSwitchItem.position) {
          const itemPos = lightSwitchItem.position;
          scene.traverse(obj => {
            // ✅ Anahtar tipi 'LightSwitch' (büyük L) — eskiden 'lightSwitch' (küçük) ile
            // kontrol ediliyordu, hiç eşleşmiyordu → son anahtarın id'si bulunamayıp yanlış
            // (yeni) id atanıyordu → LightingCommand.undo onu silemeyip leftover bırakıyordu.
            if (!foundObj && (obj.userData?.type === 'LightSwitch' || obj.userData?.type === 'lightSwitch') && obj.userData?.id) {
              const objPos = obj.position;
              if (Math.abs(objPos.x - itemPos.x) < 5 && Math.abs(objPos.y - itemPos.y) < 5) {
                foundObj = obj;
              }
            }
          });
        }

        const finalId = (foundObj && foundObj.userData?.id) || compId || lightSwitchItem?.uuid || lightSwitchItem?.id || generateUniqueId('lightSwitch');
        lightSwitchIds.push(finalId);

        const pos = (foundObj && foundObj.position) || lightSwitchItem?.position || { x: 0, y: 0, z: 0 };
        const rot = (foundObj && foundObj.rotation && foundObj.rotation.z) || lightSwitchItem?.rotation?.z || 0;
        const typ = lightSwitchItem?.type || foundObj?.userData?.subtype || 'normal';
        const rotateVal = lightSwitchItem?.rotateValue || 0; // ✅ R tuşu ile eklenen rotasyon

        // ✅ LightSwitch restore bilgilerini kaydet
        lightSwitchInfos.push({
          id: compId,
          position: { x: pos.x, y: pos.y, z: pos.z || 0 },
          rotation: rot,
          type: typ,
          stretchLength: 0,
          rotateValue: rotateVal, // ✅ R tuşu rotasyonu
          verticesAdded: 1 // ✅ Lighting için backward projection desteği yok, her zaman 1 vertex
        });
      });

      
      

      // ✅ Buat bilgilerini topla (UNDO/REDO için)
      const buatInfos = [];
      scene.traverse(obj => {
        if (obj.userData?.id && buatIdsMap[obj.userData.id]) {
          buatInfos.push({
            id: obj.userData.id,
            position: { x: obj.position.x, y: obj.position.y, z: obj.position.z || 0 },
            buatType: obj.userData.buatType || 'circle',
            type: obj.userData.type,
            lightSwitchLoadId: obj.userData.lightSwitchLoadId,
            wireID: obj.userData.wireID,
            panelID: obj.userData.panelID,
            function: obj.userData.function
          });
        }
      });

      const sceneData = {
        wireId: feederId,
        wireUUID: line.uuid,
        wireID: wireID, // ✅ Buat'lar için wireID ekle
        branchId: branchID, // ✅ UNDO için branchID ekle (panelExitManager eşleştirmesi)
        lightSwitchIds: lightSwitchIds,
        lightSwitchInfos: lightSwitchInfos, // ✅ LightSwitch restore bilgileri
        buatInfos: buatInfos, // ✅ Buat restore bilgileri (UNDO/REDO için)
        buatIds: buatIdsList, // ✅ Buat ID'leri
        vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z || 0 })),
        restoreVertices: (originalVertices || finalVertices).map(v => ({ x: v.x, y: v.y, z: v.z || 0 })), // ✅ Orijinal vertices (simplify edilmemiş)
        restoreSource: source ? {
          type: source.type,
          p: source.p ? { ...source.p } : null,
          no: source.no,
          // ✅ Machine pattern: panelID ve UUID en üst seviyede olmalı (useBaseWiring için)
          UUID: source.p?.UUID || source.no,
          panelID: source.p?.panelID || source.userData?.sourceObject?.userData?.id || null,
          // ✅ Dallanma (vertex/lightingBuat) kaynak bilgilerini koru — yoksa re-draw'da
          // confirmPlacement parentId'yi bulamayıp (source.parent kayıp) partial Enter'da
          // hat finalize olmuyor, undo resetleniyordu.
          lightingLoadId: source.lightingLoadId ?? null,
          wireID: source.wireID ?? null,
          parent: source.parent ? {
            panelID: source.parent.panelID ?? null,
            lightingLoadId: source.parent.lightingLoadId ?? null,
            id: source.parent.id ?? null,
            wireID: source.parent.wireID ?? null,
          } : null,
        } : null,
        restoreCommandType: COMMANDS.DRAW_LIGHT,
        restoreCallback: restoreCallback,
        // ✅ REDO için renk bilgisi
        color: parsedWire.colorNumber
      };

      // ✅ Command'a doğru component ve feeder verisi geç (redo için gerekli)
      // ⚠️ Branch update durumunda commandFeederData=null, bu durumda wireData fallback olarak KULLANILMAMALI
      const LightingCommand = require('../../../modules/undoRedo').LightingCommand;
      const cmd = new LightingCommand('ADD', commandComponentData || { id: lightingLoadId }, commandFeederData, sceneData);

      undoRedoManager.addCommand(cmd);

      undoRedoManager.commitTransaction(tx);
      

      // End draw operation so panel returns to normal and clicks will open the panel menu
      try {
        dispatch(resetOperation());
        dispatch(clearSelection());
        wiringActiveRef.current = false;
      } catch (e) {
        // Could not reset operation after lighting wire add
      }
    }

    // Make absolutely sure we exit draw mode so panels are clickable even if undo manager
    try {
      dispatch(resetOperation());
      dispatch(clearSelection());
      wiringActiveRef.current = false;
    } catch (e) {
      // ignore
    }

    return { wireId: feederId, line }; // ✅ Line'ı döndür
  };

  // === Wire çizimi bittiğinde Redux'a feeder kaydı
  const handleWireEnd = (vertices, lightSwitches, source, tempLineUUID) => {
    return confirmPlacement(vertices, lightSwitches, source, tempLineUUID);
  };

  // === Ok ile kat geçişinde (ok hat sonu) anahtarlara buat ekle ===
  // Normal finalize'da son anahtar yuvarlak/diğerleri kare buat alır. Ok hattında
  // hat sonu ANAHTAR DEĞİL OK olduğu için TÜM anahtarlara buat eklenir.
  // useBuatTypeSync tipi (yuvarlak/kare) hat sayısına göre düzeltir.
  const handleArrowFinalize = ({ items, wireId, panelID, color }) => {
    if (!Array.isArray(items) || !items.length) return;
    const buatColor = color ?? parsedWire.colorNumber;
    items.forEach((sw) => {
      if (!sw?.position) return;
      const buat = singleCircleBuatForLight(
        scene,
        { x: sw.position.x, y: sw.position.y },
        buatColor,
        sw.uuid
      );
      buat.userData = {
        ...buat.userData,
        id: generateUniqueId('buat'),
        type: 'buatLightingCircle',
        buatType: 'circle',
        function: 'simpleLighting',
        wireID: wireId || null,
        panelID: panelID || null,
      };
      scene.add(buat);
    });
  };

  // === Aydınlatma eklendiğinde (Space) çalışacak callback
  const handleItemAdd = (item) => {
    // LightSwitch added via Space key
  };

  // === Ana useEffect
  useEffect(() => {
    if (commandType !== COMMANDS.DRAW_LIGHT || !scene || !camera || !renderer) return;
    dispatch(setCommandMessage('Soket hattı çizin, Space ile soket ekleyin, Enter ile bitirin.'));

    sceneRef.current = scene;

    // Panel çıkışlarını ve mevcut aydınlatma buatlarını snap noktası olarak al
    dynamicSnapPoints.current = [];
    scene.traverse(obj => {
      // Panel çıkışları
      if (obj.userData?.connectionPoints) {
        obj.userData.connectionPoints.forEach(p => {
          if (p.type === 'panelCon_out') {
            const worldPos = p.position.clone().applyMatrix4(obj.matrixWorld);
            dynamicSnapPoints.current.push({
              no: obj.uuid,
              position: worldPos,
              type: 'panelConnection',
              p: p // Panel connection point bilgisi
            });
          }
        });
      }

      // Mevcut aydınlatma buatları (dallanma için)
      if (obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') {
        dynamicSnapPoints.current.push({
          position: obj.position.clone(),
          type: 'lightingBuat',
          parent: obj.parent,
          no: obj.uuid,
          userData: obj.userData,
          // Lighting buat'tan parent bilgilerini al
          lightingLoadId: obj.userData?.lightingLoadId,
          wireID: obj.userData?.wireID,
          panelID: obj.userData?.panelID
        });
      }

      // Aydınlatma hattı vertex'leri (dallanma için)
      if (obj.userData?.function === 'simpleLighting' && obj.userData?.vertices) {
        obj.userData.vertices.forEach((vertex, index) => {
          dynamicSnapPoints.current.push({
            position: new THREE.Vector3(vertex.x, vertex.y, vertex.z || 0),
            type: index === 0 ? 'start' : (index === obj.userData.vertices.length - 1 ? 'end' : 'vertex'),
            parent: obj.userData, // userData objesini parent olarak geç
            no: obj.uuid
          });
        });
      }
    });

    wiringActiveRef.current = true;

    // ✅ Cleanup: commandType değiştiğinde wiring'i devre dışı bırak
    return () => {
      wiringActiveRef.current = false;
    };
  }, [commandType, scene, camera, renderer]);

  useComponentWiring(
    scene,
    camera,
    renderer,
    [...snapPoints, ...dynamicSnapPoints.current],
    {
      ...lightingConfig, // 🆕 Config'i doğrudan geçir
      enabled: () => wiringActiveRef.current,
      onWireEnd: handleWireEnd,
      onArrowFinalize: handleArrowFinalize,
      onSocketPlace: handleLightSwitchPlace,
      onItemAdd: handleItemAdd,
      onSocketRemove: handleLightSwitchRemove,
      onPreviewUpdate: () => {},
      onWireCancel: () => { wiringActiveRef.current = false; },
      undoRedoManagerRef: undoRedoManagerRef, // ✅ Undo/Redo manager referansı
      isRedo: operationData?.isRedo || false // ✅ REDO mode: simplify devre dışı bırakılır
    }
  );

};

export default useLighting;